import {
    isMarketplace,
    isRuntimeSupportedMarketplace,
    SUPPORTED_RUNTIME_MARKETPLACES,
    type RuntimeSupportedMarketplace,
} from '@/shared/marketplace';

export type UnsupportedMarketplaceBoundary = 'runtime_route' | 'write_boundary';
export type RequestedMarketplaceClass = 'known_supported' | 'known_unsupported' | 'unknown_other';

export type UnsupportedMarketplacePayload = {
    rejected: true;
    code: 'unsupported_marketplace';
    boundary: UnsupportedMarketplaceBoundary;
    requestedMarketplace: string;
    supportedMarketplaces: string[];
    message: string;
    sideEffectsExecuted: false;
};

const UNKNOWN_REQUESTED_MARKETPLACE = 'unknown';

const toRequestedMarketplace = (requestedMarketplace: unknown): string => {
    if (typeof requestedMarketplace !== 'string') {
        return UNKNOWN_REQUESTED_MARKETPLACE;
    }

    const normalized = requestedMarketplace.trim();
    return normalized || UNKNOWN_REQUESTED_MARKETPLACE;
};

export const classifyRequestedMarketplace = (requestedMarketplace: unknown): RequestedMarketplaceClass => {
    if (typeof requestedMarketplace !== 'string') {
        return 'unknown_other';
    }

    if (isRuntimeSupportedMarketplace(requestedMarketplace)) {
        return 'known_supported';
    }

    if (isMarketplace(requestedMarketplace)) {
        return 'known_unsupported';
    }

    return 'unknown_other';
};

export const toSupportedMarketplacesList = (): RuntimeSupportedMarketplace[] => {
    return [...SUPPORTED_RUNTIME_MARKETPLACES];
};

export const buildUnsupportedMarketplaceMessage = (requestedMarketplace: unknown): string => {
    return `Unsupported marketplace platform: ${toRequestedMarketplace(requestedMarketplace)}`;
};

export const createUnsupportedMarketplacePayload = ({
    requestedMarketplace,
    boundary,
    supportedMarketplaces = toSupportedMarketplacesList(),
}: {
    requestedMarketplace: unknown;
    boundary: UnsupportedMarketplaceBoundary;
    supportedMarketplaces?: RuntimeSupportedMarketplace[];
}): UnsupportedMarketplacePayload => {
    const normalizedRequestedMarketplace = toRequestedMarketplace(requestedMarketplace);

    return {
        rejected: true,
        code: 'unsupported_marketplace',
        boundary,
        requestedMarketplace: normalizedRequestedMarketplace,
        supportedMarketplaces: [...supportedMarketplaces],
        message: buildUnsupportedMarketplaceMessage(normalizedRequestedMarketplace),
        sideEffectsExecuted: false,
    };
};

const isStringArray = (value: unknown): value is string[] => {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
};

export const isUnsupportedMarketplacePayload = (value: unknown): value is UnsupportedMarketplacePayload => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const payload = value as Partial<UnsupportedMarketplacePayload>;

    return (
        payload.rejected === true &&
        payload.code === 'unsupported_marketplace' &&
        (payload.boundary === 'runtime_route' || payload.boundary === 'write_boundary') &&
        typeof payload.requestedMarketplace === 'string' &&
        isStringArray(payload.supportedMarketplaces) &&
        typeof payload.message === 'string' &&
        payload.sideEffectsExecuted === false
    );
};
